import {
  Berth,
  Geofence,
  Leg,
  PowerOutage,
  ReeferKpiByModelResponse,
  SensorPoint,
  TemperatureBreach,
  TsGpsPoint,
} from 'seacube-backend/src/api/client';
import { v4 as uuidv4 } from 'uuid';
import { proxy, ref } from 'valtio';

import { Dayjs } from '@/dayjs';

import { ReeferKpiByModel } from '@/hooks';

type PowerOutageMapping = {
  [key: string]: PowerOutage;
};
type TemperatureBreachMapping = {
  [key: string]: TemperatureBreach;
};

export interface LinechartPoint extends SensorPoint {
  power_outage_id: null | string;
  temperature_breach_id: null | string;
}

export interface ReeferKpiByModelWithPredicted extends ReeferKpiByModel {
  predicted_energy_kWh: number;
  predicted_co2_emissions: number;
}

export interface LegAugmented extends Leg {
  events: {
    powerOutage: number;
    temperatureBreach: number;
  };
}

export enum FeftchSpan {
  WEEK = '1week',
  MONTH = '1month',
  MONTH_3 = '3months',
  MONTH_6 = '6months',
}

// Message type pour le chat
export type Message = {
  role: 'user' | 'ai';
  text: string;
  timestamp: number;
};

export interface ReeferInsightsStore {
  // data
  isLoading: boolean;
  conversationId: string | null;
  initialFetch: boolean;
  fetchLimits?: [Dayjs, Dayjs];
  mapRef: any;
  gpsData: {
    highlightedIndex?: number;
    filtered: TsGpsPoint[];
    raw: TsGpsPoint[];
  };
  mapLayers: {
    zoomPoint: null | {
      latitude: number;
      longitude: number;
    };
    berths: {
      data: Berth[];
      display: boolean;
    };
    geofences: {
      data: Geofence[];
      display: boolean;
      selectedTypes: string[];
    };
  };
  sensorData: {
    filtered: SensorPoint[];
    raw: SensorPoint[];
    highlightedIndex?: number;
    showFuel: boolean;
  };
  tripFooter: {
    progress: number;
    timestamp?: number;
    setpoint?: number;
    return?: number;
  };
  linechart: {
    data: LinechartPoint[];
    displayGps: boolean;
    powerOutageMapping: PowerOutageMapping;
    temperatureBreachMapping: TemperatureBreachMapping;
  };

  powerOutages: {
    raw: PowerOutage[];
    filtered: PowerOutage[];
    minDuration: number;
    minDeviation: number;
  };
  temperatureBreaches: {
    raw: TemperatureBreach[];
    filtered: TemperatureBreach[];
    minDuration: number;
    minDeviation: number;
  };
  reeferKpi?: ReeferKpiByModelWithPredicted;
  legs: Leg[];
  legsAugmented: LegAugmented[];
  selectedLegs: Leg[];
  selectedLegId?: string;
  selectedLegIds: string[];
  tsFilterRange?: [number, number];
  // lastSensorValueTimeDiff?: number;
  tsFetchRange?: [number, number];
  fetchSpan: FeftchSpan;
  reeferId?: string;
  layout: {
    showGps: boolean;
  };
  // chat messages
  chatMessages: Message[];
}

let store = proxy<ReeferInsightsStore>({
  reeferId: undefined,
  conversationId: null,
  initialFetch: false,
  fetchLimits: undefined,
  tripFooter: {
    progress: 0,
    timestamp: undefined,
  },
  layout: {
    showGps: false,
  },
  mapLayers: {
    zoomPoint: null,
    berths: {
      data: [],
      display: true,
    },
    geofences: {
      data: [],
      display: true,
      selectedTypes: [
        'REEFER_STORAGE',
        'DRY_STORAGE',
        'CUSTOM',
        'PHYTO',
        'RAIL_TERMINAL',
        'LOADING_QUAY',
      ],
    },
  },
  isLoading: false,
  mapRef: ref([]),
  gpsData: {
    filtered: [],
    raw: [],
  },
  sensorData: {
    filtered: [],
    raw: [],
    showFuel: false,
  },
  linechart: {
    data: [],
    displayGps: false,
    powerOutageMapping: {},
    temperatureBreachMapping: {},
  },

  legs: [],
  fetchSpan: FeftchSpan.MONTH,
  legsAugmented: [],
  selectedLegs: [],
  powerOutages: {
    filtered: [],
    raw: [],
    minDeviation: 3,
    minDuration: 3,
  },
  temperatureBreaches: {
    filtered: [],
    raw: [],
    minDeviation: 3,
    minDuration: 3,
  },
  get selectedLegIds() {
    return this.selectedLegs.map((e: Leg) => e.id);
  },
  // get lastSensorValueTimeDiff() {
  //   if (!this.sensorData.highlightedIndex) return;
  //   if (!this.gpsData.highlightedIndex) return;
  //   return (
  //     this.sensorData.filtered[this.sensorData.highlightedIndex].timestamp -
  //     this.gpsData.filtered[this.gpsData.highlightedIndex].timestamp
  //   );
  // },
  chatMessages: [],
});

export const resetDisplayedData = () => {
  store.gpsData.filtered = [];
  store.sensorData.filtered = [];
  store.linechart.data = [];
  store.powerOutages.filtered = [];
  store.temperatureBreaches.filtered = [];
  store.legs = [];
  store.reeferKpi = undefined;
};

// subscribe(store, () => {
//   if (import.meta.hot) {
//     import.meta.hot.data.store = store;
//   }
// });

// if (import.meta.hot) {
//   import.meta.hot.on('vite:afterUpdate', () => {
//     store = proxy(import.meta.hot?.data.store);
//   });
// }

export const setConversationId = () => {
  store.conversationId = uuidv4();
  store.chatMessages = [];
};

export { store };
